import React, { useState } from 'react';
import { AZURE } from '../services/api';
import { BRAND_GREEN, BRAND_GOLD } from '../theme/sucronTheme';


/**
 * DiagnosticPage — Testa todos os endpoints RBAC e de esportes
 */
const DiagnosticPage = () => {
  const [results, setResults] = useState({});
  const [testing, setTesting] = useState(false);

  const endpoints = [
    // Manager
    { name: 'Manager KPIs', url: '/manager/kpis?tenant_id=1', method: 'GET' },
    { name: 'Manager Members', url: '/rbac/club/1/members', method: 'GET' },
    
    // Coach
    { name: 'Coach Area', url: '/coach/dashboard', method: 'GET' },
    
    // Athlete
    { name: 'Athlete Training Today', url: '/my/training/swimming/today', method: 'GET' },
    { name: 'Athlete Swimming Results', url: '/my/swimming/results', method: 'GET' },
    
    // Sports
    { name: 'Swimming Clubs', url: '/swimming/clubs', method: 'GET' },
    { name: 'Swimming Competitions', url: '/swimming/competitions', method: 'GET' },
    { name: 'Swimming Swimmers', url: '/swimming/swimmers', method: 'GET' },
    { name: 'Football Leagues', url: '/football/leagues', method: 'GET' },
    { name: 'Football Matches', url: '/football/matches', method: 'GET' },
    
    // Social
    { name: 'Organizations', url: '/organizations', method: 'GET' },
    { name: 'Venues', url: '/venues', method: 'GET' },
    { name: 'People Directory', url: '/people/directory', method: 'GET' },
  ];

  const testEndpoint = async (endpoint) => {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 5000); // 5s timeout
    
    const startTime = Date.now();
    try {
      const response = await AZURE.request({
        method: endpoint.method,
        url: endpoint.url,
        signal: controller.signal
      });
      clearTimeout(timeoutId);
      const duration = Date.now() - startTime;
      
      return {
        status: 'success',
        statusCode: response.status,
        duration: `${duration}ms`,
        dataSize: JSON.stringify(response.data).length
      };
    } catch (err) {
      clearTimeout(timeoutId);
      const duration = Date.now() - startTime;
      
      return {
        status: 'error',
        error: err.code === 'ECONNABORTED' ? 'Timeout (5s)' : 
               err.response?.status === 404 ? '404 Not Found' :
               err.response?.status === 500 ? '500 Server Error' :
               err.response?.status === 401 ? '401 Unauthorized' :
               err.message || 'Unknown error',
        duration: `${duration}ms`
      };
    }
  };

  const runDiagnostics = async () => {
    setTesting(true);
    setResults({});
    
    for (const endpoint of endpoints) {
      const result = await testEndpoint(endpoint);
      setResults(prev => ({
        ...prev,
        [endpoint.name]: result
      }));
    }
    
    setTesting(false);
  };

  const getStatusColor = (status) => {
    if (status === 'success') return BRAND_GREEN;
    return '#dc2626';
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#f8fafc' }}>
      <div className="max-w-5xl mx-auto px-4 py-8">
        <div className="bg-white rounded-xl border border-gray-200 mb-6 p-6">
          <h1 className="text-3xl font-extrabold mb-2" style={{ color: BRAND_GREEN }}>
            🔍 Diagnóstico de Endpoints
          </h1>
          <p className="text-gray-600 mb-4">
            Testa todos os endpoints RBAC e de esportes para identificar problemas
          </p>
          
          <button
            onClick={runDiagnostics}
            disabled={testing}
            className="px-6 py-3 rounded-lg font-bold text-white transition-all"
            style={{ backgroundColor: testing ? '#9ca3af' : BRAND_GREEN }}
          >
            {testing ? '⏳ Testando...' : '▶️ Executar Diagnóstico'}
          </button>
        </div>

        <div className="space-y-3">
          {endpoints.map(endpoint => {
            const result = results[endpoint.name];
            const isLoading = testing && !result;
            
            return (
              <div 
                key={endpoint.name}
                className="bg-white rounded-lg border border-gray-200 p-4"
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <h3 className="font-bold text-sm" style={{ color: BRAND_GREEN }}>
                      {endpoint.name}
                    </h3>
                    <p className="text-xs text-gray-500 font-mono">
                      {endpoint.method} {endpoint.url}
                    </p>
                  </div>
                  
                  {isLoading && (
                    <div className="text-sm text-gray-400">Aguardando...</div>
                  )}
                  
                  {result && (
                    <div className="text-right">
                      <div 
                        className="text-sm font-bold mb-1"
                        style={{ color: getStatusColor(result.status) }}
                      >
                        {result.status === 'success' ? '✅ OK' : '❌ ERRO'}
                      </div>
                      <div className="text-xs text-gray-500">
                        {result.duration}
                      </div>
                      {result.error && (
                        <div className="text-xs text-red-600 mt-1">
                          {result.error}
                        </div>
                      )}
                      {result.statusCode && (
                        <div className="text-xs text-gray-400">
                          HTTP {result.statusCode}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
        
        {Object.keys(results).length > 0 && !testing && (
          <div className="mt-6 bg-white rounded-xl border border-gray-200 p-6">
            <h2 className="text-lg font-bold mb-4" style={{ color: BRAND_GREEN }}>
              📊 Resumo
            </h2>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <div className="text-2xl font-bold" style={{ color: BRAND_GREEN }}>
                  {Object.values(results).filter(r => r.status === 'success').length}
                </div>
                <div className="text-sm text-gray-600">Endpoints OK</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-red-600">
                  {Object.values(results).filter(r => r.status === 'error').length}
                </div>
                <div className="text-sm text-gray-600">Endpoints com Erro</div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DiagnosticPage;
